import type { ControllerActivity, FixtureValues, LiveState, TransportState } from './types';

function stoppedTransport(): TransportState {
  return { state: 'stopped', positionSeconds: 0, durationSeconds: 0, loop: false, snapshotAt: 0 };
}

function paramsFrom(activity: ControllerActivity): Record<string, unknown> | undefined {
  if (!activity.intentJson || !['editing', 'committed', 'pending'].includes(activity.phase)) return undefined;
  try {
    const parsed = JSON.parse(activity.intentJson) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : undefined;
  } catch {
    return undefined;
  }
}

function numericValues(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, number] => Number.isFinite(entry[1])));
}

function fixtureIntent(fixture: FixtureValues, params: Record<string, unknown>): FixtureValues {
  const values = numericValues(params.values);
  const rawElementValues = params.elementValues && typeof params.elementValues === 'object' && !Array.isArray(params.elementValues)
    ? (params.elementValues as Record<string, unknown>)[fixture.deviceId]
    : undefined;
  const elementValues = rawElementValues && typeof rawElementValues === 'object' && !Array.isArray(rawElementValues)
    ? rawElementValues as Record<string, unknown>
    : {};
  const fixtureIds = Array.isArray(params.fixtures) ? params.fixtures.filter((id): id is string => typeof id === 'string') : [];
  const elements = params.elements && typeof params.elements === 'object' && !Array.isArray(params.elements)
    ? params.elements as Record<string, unknown>
    : {};
  const elementIds = Array.isArray(elements[fixture.deviceId])
    ? (elements[fixture.deviceId] as unknown[]).filter((index): index is number => Number.isInteger(index))
    : [];
  if (fixtureIds.includes(fixture.deviceId) && Object.keys(values).length) {
    return { ...fixture, values: { ...fixture.values, ...values } };
  }
  if ((!elementIds.length && !Object.keys(elementValues).length) || !fixture.elements) return fixture;
  const selected = new Set(elementIds);
  return {
    ...fixture,
    elements: fixture.elements.map((element) => {
      const exact = numericValues(elementValues[String(element.index)]);
      const common = selected.has(element.index) ? values : {};
      return Object.keys(common).length || Object.keys(exact).length
        ? { ...element, values: { ...element.values, ...common, ...exact } }
        : element;
    }),
  };
}

/** Apply a short-lived collaboration preview; controller results remain authoritative. */
export function applyControllerIntent(state: LiveState | undefined, activity: ControllerActivity): LiveState | undefined {
  const params = paramsFrom(activity);
  if (!state || !params) return state;

  if (activity.command === 'transport') {
    const action = typeof params.action === 'string' ? params.action : '';
    const transport = { ...(state.transport ?? stoppedTransport()) };
    if (action === 'play') transport.state = 'playing';
    else if (action === 'pause') transport.state = 'paused';
    else if (action === 'stop') transport.state = 'stopped';
    else if (action === 'seek' && Number.isFinite(params.positionSeconds)) transport.positionSeconds = Number(params.positionSeconds);
    else if (action === 'set_loop' && typeof params.loop === 'boolean') transport.loop = params.loop;
    else return state;
    return { ...state, transport };
  }

  if (activity.command === 'load_score' && typeof params.scoreId === 'string') {
    return {
      ...state,
      transport: {
        ...(state.transport ?? stoppedTransport()),
        state: 'stopped',
        scoreId: params.scoreId,
        versionId: typeof params.versionId === 'string' ? params.versionId : undefined,
        positionSeconds: 0,
      },
    };
  }

  if (activity.command === 'set_fixture_params') {
    return { ...state, fixtures: state.fixtures.map((fixture) => fixtureIntent(fixture, params)) };
  }

  if (activity.command === 'blackout' && typeof params.on === 'boolean') {
    return { ...state, blackout: params.on };
  }

  if (activity.command === 'set_output_rate' && Number.isFinite(params.hz)) {
    return { ...state, outputRateHz: Number(params.hz) };
  }

  if (activity.command === 'set_mode' && typeof params.source === 'string') {
    const takenOver = params.source !== 'stream' && params.source !== 'remote';
    return {
      ...state,
      activeSource: params.source,
      activeSourceLabel: params.source === 'panel' ? 'Scenes' : state.activeSourceLabel,
      ...(takenOver ? { stream: undefined, remoteLive: undefined } : {}),
    };
  }

  if (activity.command === 'recall_scene' && typeof params.sceneId === 'string') {
    return {
      ...state,
      activeSource: 'panel',
      activeSourceLabel: 'Scenes',
      scene: { ...(state.scene ?? {}), sceneId: params.sceneId, recalledSceneId: params.sceneId },
    };
  }

  return state;
}
